import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { Alert, Button, Empty, Money, Note, PageHeader, Panel, Spinner, Table, Tiles } from '../ui';

/**
 * R-01 Dashboard — DB-01…DB-07, DB-12, DB-13.
 *
 * The SERVER composes this page (`dashboard/tiles.ts`). A tile that is absent from the response
 * is not rendered, and its figure was never sent: a Buyer's response carries no payable at all,
 * so there is nothing here to hide and nothing a browser could reveal (DB-07). This screen draws
 * whatever arrived and decides nothing.
 *
 * Every figure links to the screen that owns it (DB-12). The one exception is DB-12's own: there
 * is no retailer budget screen, so the budget tile opens its BG-02 breakdown in place.
 *
 * Money arrives as decimal strings, per currency, and is never added up here (AN-08).
 */

interface Amount { currency: string; amount: string }
type Pending<T> = { state: 'READY'; value: T } | { state: 'NOT_YET'; waitingFor: string };
interface Budget {
  currency: string; assigned: string; available: string; releaseOn: 'SALE' | 'PAYMENT';
  inUse: { openOrders: string; inTransit: string; held: string; soldUnpaid: string; total: string };
}
interface Account {
  name: string; tier: string | null; status: string; groupName: string | null;
  subscription: { status: string; startedOn: string | null; renewsOn: string | null; daysToRenewal: number | null } | null;
  assignedBudget: Pending<string> | null;
}
interface OpenOrder { id: string; reference: string; status: string; submittedOn: string | null; dispatchBy: string | null; dispatchSource: 'promised' | 'estimated' | null }
interface Invoice { id: string; reference: string; currency: string; amount: string; issuedOn: string; dueOn: string; state: string; overdue: boolean }
interface Payment { id: string; currency: string; amount: string; receivedOn: string; bankReference: string | null }
interface Inventory {
  currency: string; wholesaleValue: string; rpValue: string; piecesOnHand: number;
  byArticle: Array<{ article: string; pieces: number }>;
}
type TileData = {
  account: Account; budget: Pending<Budget>; inventory: Pending<Inventory>; openOrders: OpenOrder[]; payable: Amount[];
  invoices: Invoice[]; paymentHistory: Payment[]; quickCart: null; quickOrders: null;
  quickInventory: null; quickSold: null;
};
type Tile = { [K in keyof TileData]: { id: K; requirement: string; drill: { page: string; route: string } | 'IN_PLACE'; data: TileData[K] } }[keyof TileData];
interface Dashboard { portal: { code: string; name: string; currency: string; timezone: string }; today: string; tiles: Tile[] }

const pick = <K extends keyof TileData>(tiles: Tile[], id: K) =>
  tiles.find((t) => t.id === id) as Extract<Tile, { id: K }> | undefined;
const routeOf = (t: Tile) => (t.drill === 'IN_PLACE' ? null : t.drill.route);

function Amounts({ list, empty }: { list: readonly Amount[]; empty: string }) {
  if (list.length === 0) return <span className="mk-dim">{empty}</span>;
  return <>{list.map((a) => <div key={a.currency}><Money amount={a.amount} currency={a.currency} /></div>)}</>;
}

export function ShopDashboard() {
  const { t } = useTranslation();
  const [budgetOpen, setBudgetOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'dashboard'], queryFn: () => api.get<Dashboard>('/shop/dashboard'),
  });
  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  const account = pick(data.tiles, 'account');
  const budget = pick(data.tiles, 'budget');
  const inventory = pick(data.tiles, 'inventory');
  const orders = pick(data.tiles, 'openOrders');
  const payable = pick(data.tiles, 'payable');
  const invoices = pick(data.tiles, 'invoices');
  const payments = pick(data.tiles, 'paymentHistory');
  const QUICK = ['quickCart', 'quickOrders', 'quickInventory', 'quickSold'];
  const quick = data.tiles.filter((x) => QUICK.includes(x.id));
  const notYet = t('shopDashboard.notYet');

  const tiles = [
    ...(budget ? [{
      k: t('shopDashboard.available'),
      v: budget.data.state === 'READY' ? <Money amount={budget.data.value.available} currency={budget.data.value.currency} /> : '—',
      s: budget.data.state === 'READY'
        ? <Button size="sm" tone="ghost" onClick={() => setBudgetOpen((o) => !o)}>{t(budgetOpen ? 'shopDashboard.hideBreakdown' : 'shopDashboard.whatHolds')}</Button>
        : notYet,
    }] : []),
    // DB-02 — a figure that could not be read is "—" and says so; 0 would claim the shelf is empty.
    ...(inventory ? [{
      k: t('shopDashboard.onHand'),
      v: inventory.data.state === 'READY' ? inventory.data.value.piecesOnHand : '—',
      s: <Link to={routeOf(inventory)!}>{t('shopDashboard.seeInventory')}</Link>,
    }] : []),
    ...(inventory && inventory.data.state === 'READY' ? [{
      k: t('shopDashboard.inventory'),
      v: <Money amount={inventory.data.value.wholesaleValue} currency={inventory.data.value.currency} />,
      s: <>{t('shopDashboard.rp')} <Money amount={inventory.data.value.rpValue} currency={inventory.data.value.currency} /></>,
    }] : []),
    ...(orders ? [{ k: t('shopDashboard.openOrders'), v: orders.data.length, s: <Link to={routeOf(orders)!}>{t('shopDashboard.seeOrders')}</Link> }] : []),
    ...(payable ? [{
      k: t('shopDashboard.payable'),
      v: <Amounts list={payable.data} empty={t('shopDashboard.nothingOwed')} />,
      s: <Link to={routeOf(payable)!}>{t('shopDashboard.seeStatement')}</Link>,
    }] : []),
  ];

  return (
    <>
      <PageHeader
        title={account ? account.data.name : t('shopDashboard.title')}
        sub={t('shopDashboard.sub', { portal: data.portal.name, currency: data.portal.currency, today: data.today })}
      />

      {account ? (
        <Panel title={t('shopDashboard.account')} pad actions={<Link to={routeOf(account)!}>{t('shopDashboard.seeAccount')}</Link>}>
          <p>
            {account.data.groupName ? <>{t('shopDashboard.group', { name: account.data.groupName })} · </> : null}
            {t('shopDashboard.tier', { tier: account.data.tier ?? '—' })}
            {account.data.assignedBudget ? (
              <> · {t('shopDashboard.assigned')}{' '}
                {account.data.assignedBudget.state === 'READY'
                  ? <Money amount={account.data.assignedBudget.value} currency={budget?.data.state === 'READY' ? budget.data.value.currency : data.portal.currency} />
                  : <span className="mk-dim">{notYet}</span>}
              </>
            ) : null}
          </p>
          {account.data.subscription ? (
            <p className="mk-hint">
              {t('shopDashboard.subscription', { status: t(`subscriptions.status.${account.data.subscription.status}`) })}
              {account.data.subscription.startedOn ? <> · {t('shopDashboard.started', { day: account.data.subscription.startedOn })}</> : null}
              {account.data.subscription.renewsOn ? <> · {t('shopDashboard.renews', { day: account.data.subscription.renewsOn })}</> : null}
            </p>
          ) : null}
        </Panel>
      ) : null}

      {tiles.length ? <Tiles items={tiles} /> : null}

      {budget && budget.data.state === 'READY' && budgetOpen ? (
        <Panel title={t('shopDashboard.breakdown')} pad>
          <Table
            rows={[
              ['openOrders', budget.data.value.inUse.openOrders],
              ['inTransit', budget.data.value.inUse.inTransit],
              ['held', budget.data.value.inUse.held],
              // BG-02 (d) — only where the budget is released on payment. A Buyer sees it: it is
              // capacity, and it answers "why can I not order" (DB-07 as amended).
              ...(budget.data.value.releaseOn === 'PAYMENT' ? [['soldUnpaid', budget.data.value.inUse.soldUnpaid] as const] : []),
              ['total', budget.data.value.inUse.total],
            ] as Array<readonly [string, string]>}
            rowKey={([k]) => k}
            columns={[
              { header: t('shopDashboard.component'), render: ([k]) => t(`shopDashboard.inUse.${k}`) },
              { header: t('shopDashboard.value'), numeric: true, render: ([, v]) => <Money amount={v} currency={budget.data.state === 'READY' ? budget.data.value.currency : ''} /> },
            ]}
          />
        </Panel>
      ) : null}
      {budget && budget.data.state === 'NOT_YET' ? <Note>{t('shopDashboard.budgetNotYet')}</Note> : null}

      {inventory && inventory.data.state === 'NOT_YET' ? <Note>{t('shopDashboard.notYet')}</Note> : null}
      {inventory && inventory.data.state === 'READY' && inventory.data.value.byArticle.length ? (
        <Panel title={t('shopDashboard.byArticle')} actions={<Link to={routeOf(inventory)!}>{t('shopDashboard.seeInventory')}</Link>}>
          <Table
            rows={inventory.data.value.byArticle}
            rowKey={(a) => a.article}
            columns={[
              { header: t('shopDashboard.colArticle'), render: (a) => a.article },
              { header: t('shopDashboard.colPieces'), numeric: true, render: (a) => a.pieces },
            ]}
          />
        </Panel>
      ) : null}

      {orders ? (
        <Panel title={t('shopDashboard.openOrders')}>
          <Table
            rows={orders.data}
            rowKey={(o) => o.id}
            columns={[
              { header: t('shopDashboard.colOrder'), render: (o) => <Link to={`/shop/orders/${o.id}`}>{o.reference}</Link> },
              { header: t('shopDashboard.colSubmitted'), render: (o) => <span className="mk-dim">{o.submittedOn ?? '—'}</span> },
              {
                header: t('shopDashboard.colDispatch'),
                render: (o) => o.dispatchBy
                  ? <>{o.dispatchBy}{o.dispatchSource === 'estimated' ? <span className="mk-hint"> {t('shopDashboard.estimated')}</span> : null}</>
                  : <span className="mk-dim">{t('shopDashboard.notConfirmed')}</span>,
              },
            ]}
            empty={<Empty title={t('shopDashboard.noOrdersTitle')} body={t('shopDashboard.noOrdersBody')} />}
          />
        </Panel>
      ) : null}

      {invoices && invoices.data.length ? (
        <Panel title={t('shopDashboard.invoices')} actions={<Link to={routeOf(invoices)!}>{t('shopDashboard.seeStatement')}</Link>}>
          <Table
            rows={invoices.data}
            rowKey={(i) => i.id}
            columns={[
              { header: t('shopDashboard.colInvoice'), render: (i) => i.reference },
              { header: t('shopDashboard.colDue'), render: (i) => <>{i.dueOn}{i.overdue ? <span className="mk-hint"> {t('shopDashboard.overdue')}</span> : null}</> },
              { header: t('shopDashboard.value'), numeric: true, render: (i) => <Money amount={i.amount} currency={i.currency} /> },
            ]}
          />
        </Panel>
      ) : null}

      {payments && payments.data.length ? (
        <Panel title={t('shopDashboard.payments')} actions={<Link to={routeOf(payments)!}>{t('shopDashboard.seeStatement')}</Link>}>
          <Table
            rows={payments.data}
            rowKey={(p) => p.id}
            columns={[
              { header: t('shopDashboard.colReceived'), render: (p) => p.receivedOn },
              { header: t('shopDashboard.colReference'), render: (p) => <span className="mk-dim">{p.bankReference ?? '—'}</span> },
              { header: t('shopDashboard.value'), numeric: true, render: (p) => <Money amount={p.amount} currency={p.currency} /> },
            ]}
          />
        </Panel>
      ) : null}

      {quick.length ? (
        <Panel title={t('shopDashboard.quick')} pad>
          {/* Each link is its own item in a spaced row (`.mk-quick`), not a word in a run of
              text: the separation is the only thing that shows where one link ends. */}
          <p className="mk-quick">
            {quick.map((q) => <Link key={q.id} to={routeOf(q)!}>{t(`shopDashboard.${q.id}`)}</Link>)}
          </p>
        </Panel>
      ) : null}
    </>
  );
}
